const TOKEN_KEY = "floraiq_access_token";
const USER_KEY = "floraiq_user";
const PERMS_KEY = "floraiq_permissions";

export interface AuthUser {
  id: number;
  uuid: string;
  name: string;
  email: string;
  role: string | null;
}

export interface UserPermissionsData {
  role: string | null;
  role_name: string | null;
  permissions: string[];
}

let _token: string | null = null;
let _user: AuthUser | null = null;
let _permissions: UserPermissionsData | null = null;
let _listeners: Array<() => void> = [];

function notify() {
  _listeners.forEach((fn) => fn());
}

export function getToken(): string | null {
  if (_token) return _token;
  try {
    _token = sessionStorage.getItem(TOKEN_KEY);
  } catch {
    // SSR or blocked storage
  }
  return _token;
}

export function getUser(): AuthUser | null {
  if (_user) return _user;
  try {
    const raw = sessionStorage.getItem(USER_KEY);
    if (raw) _user = JSON.parse(raw);
  } catch {
    // ignore
  }
  return _user;
}

export function getPermissions(): UserPermissionsData | null {
  if (_permissions) return _permissions;
  try {
    const raw = sessionStorage.getItem(PERMS_KEY);
    if (raw) _permissions = JSON.parse(raw);
  } catch {
    // ignore
  }
  return _permissions;
}

export function hasPermission(permission: string): boolean {
  const perms = getPermissions();
  if (!perms) return false;
  return perms.permissions.includes(permission);
}

export function hasAnyPermission(...permissions: string[]): boolean {
  const perms = getPermissions();
  if (!perms) return false;
  return permissions.some((p) => perms.permissions.includes(p));
}

export function isAuthenticated(): boolean {
  return getToken() !== null;
}

export function setAuth(token: string, user: AuthUser): void {
  _token = token;
  _user = user;
  try {
    sessionStorage.setItem(TOKEN_KEY, token);
    sessionStorage.setItem(USER_KEY, JSON.stringify(user));
  } catch {
    // storage unavailable
  }
  notify();
}

export function setPermissions(perms: UserPermissionsData): void {
  _permissions = perms;
  try {
    sessionStorage.setItem(PERMS_KEY, JSON.stringify(perms));
  } catch {
    // storage unavailable
  }
  notify();
}

export function clearAuth(): void {
  _token = null;
  _user = null;
  _permissions = null;
  try {
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(USER_KEY);
    sessionStorage.removeItem(PERMS_KEY);
  } catch {
    // ignore
  }
  notify();
}

export function onAuthChange(listener: () => void): () => void {
  _listeners.push(listener);
  return () => {
    _listeners = _listeners.filter((fn) => fn !== listener);
  };
}

/** Rota de login que, apos entrar, devolve o usuario para `from`. */
export function loginPath(from?: string): string {
  return from && safeReturnPath(from) ? `/login?next=${encodeURIComponent(from)}` : "/login";
}

// So caminho relativo do proprio site: "//host" ou "https://..." viraria
// redirecionamento aberto para fora do sistema.
function safeReturnPath(path: string): boolean {
  return path.startsWith("/") && !path.startsWith("//") && !path.startsWith("/login");
}

/**
 * Para onde ir apos o login: a tela de origem; sem ela, o coletor quando o site
 * roda instalado (PWA, start_url /coletor), e o painel no navegador comum.
 */
export function postLoginPath(search: string): string {
  const next = new URLSearchParams(search).get("next");
  if (next && safeReturnPath(next)) return next;
  const standalone =
    window.matchMedia?.("(display-mode: standalone)").matches ||
    (navigator as unknown as { standalone?: boolean }).standalone === true;
  return standalone ? "/coletor" : "/";
}
